import React from 'react';
import {Image} from 'antd-mobile';
import i18n from '@/i18n';
import {toPriceStr} from '@/components/utils/converter';
import SectionCard from './section-card';
import {
  TodayEarningsChartItemRes,
  TodayEarningsChart,
} from '@/common-pages/proxy/proxy.type';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';

const defaultHeaderImg = require('@components/assets/icons/default-header.webp');
const sort1Icon = require('@/pages/agent/assets/sort-1.webp');
const sort2Icon = require('@/pages/agent/assets/sort-2.webp');
const sort3Icon = require('@/pages/agent/assets/sort-3.webp');

interface RankingSectionProps {
  earningsList: TodayEarningsChartItemRes[];
  chart?: TodayEarningsChart;
  loading: boolean;
}

const RankingSection: React.FC<RankingSectionProps> = ({
  earningsList,
  chart,
  loading,
}) => {
  return (
    <SectionCard>
      <div className="flex items-center mb-2">
        <div className="w-1 h-4 rounded bg-gradient-to-b from-[#009CDA] to-[#0048FF]" />
        <span className="text-sm text-main ml-2">
          {i18n.t('proxy.home.commission-ranking')}
        </span>
      </div>
      <div className="flex flex-col">
        {loading && (
          <div className="flex justify-center py-2">
            <ProgressiveCircularLoading size={20} />
          </div>
        )}
        {!loading &&
          earningsList.map((item, index) => (
            <div
              key={index}
              className="flex justify-between h-10 items-center px-1">
              <div className="flex items-center">
                {index < 3 ? (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <Image
                      src={
                        index === 0
                          ? sort1Icon
                          : index === 1
                          ? sort2Icon
                          : sort3Icon
                      }
                      className="w-full h-full object-contain"
                      alt={`Rank ${index + 1}`}
                    />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded bg-[#E5E5E5] text-center text-xs font-bold leading-5">
                    {index + 1}
                  </div>
                )}
                <div className="ml-3 mr-1">
                  <Image
                    src={item?.headImg || defaultHeaderImg}
                    className="w-6 h-6 rounded-full object-cover"
                    fallback={defaultHeaderImg}
                  />
                </div>
                <span className="ml-1 text-xs">{item?.phone}</span>
              </div>
              <span className="text-base font-bold font-din text-main">
                {toPriceStr(
                  item?.commissionAmount ? +item.commissionAmount : 0,
                  {fixed: 1},
                )}
              </span>
            </div>
          ))}
        {!loading && earningsList.length === 0 && (
          <div className="text-center text-acc text-xs py-3">--</div>
        )}
      </div>

      <div className="mt-2 px-1 py-2 border-t w-full">
        <div className="flex flex-row items-center w-full">
          <div className="flex flex-col h-7 w-6 justify-center items-center">
            <span className="text-sm text-main font-bold">
              {chart?.rank || '-'}
            </span>
            <span className="text-10 text-acc din font-bold">
              {i18n.t('proxy.home.me')}
            </span>
          </div>
          <div className="flex flex-row flex-1 items-center ml-3">
            <Image
              src={defaultHeaderImg}
              className="w-7 h-7 rounded-full object-cover"
              fallback={defaultHeaderImg}
            />
            <div className="text-10 ml-2 flex-1">
              <span>
                {i18n.t('proxy.home.agents-whose-commissions-exceed')}
              </span>
              <span className="text-[#E20000] font-bold ml-1">
                {chart?.exceed || '-'}
              </span>
            </div>
          </div>
          <span className="text-base font-bold font-din text-main text-right">
            {toPriceStr(chart?.commissionAmount ? +chart.commissionAmount : 0, {
              fixed: 1,
            })}
          </span>
        </div>
      </div>
    </SectionCard>
  );
};

export default RankingSection;
